import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import { useAuth } from '@/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, Plus } from 'lucide-react'
import { useEncuestaDetail, useActualizarEncuesta } from '../hooks/useEncuestas'
import { useCrearCampo, useActualizarCampo, useEliminarCampo } from '../hooks/useCampos'
import { EncuestaForm } from '../components/EncuestaForm'
import { CampoCard } from '../components/CampoCard'
import { CampoDialog } from '../components/CampoDialog'
import type { EncuestaUpdatePayload, Campo, CampoCreatePayload, CampoUpdatePayload } from '../types'

export function EncuestaEditPage() {
  const { id } = useParams<{ id: string }>()
  const encuestaId = Number(id)
  const navigate = useNavigate()
  const { user } = useAuth()
  const canEdit = user?.is_superuser || ['admin', 'encuestas_admin'].some(g => user?.groups?.includes(g))

  useDocumentTitle('Editar Encuesta')
  const { setOverride, clearOverride } = useBreadcrumb()

  const { data: encuesta, isLoading } = useEncuestaDetail(encuestaId)

  useEffect(() => {
    if (encuesta?.titulo) {
      setOverride(`/encuestas/${id}`, encuesta.titulo)
    }
    return () => { clearOverride(`/encuestas/${id}`) }
  }, [encuesta?.titulo, id, setOverride, clearOverride])
  const actualizarMutation = useActualizarEncuesta()
  const crearCampoMutation = useCrearCampo(encuestaId)
  const actualizarCampoMutation = useActualizarCampo(encuestaId)
  const eliminarCampoMutation = useEliminarCampo(encuestaId)


  const [campoDialogOpen, setCampoDialogOpen] = useState(false)
  const [editingCampo, setEditingCampo] = useState<Campo | null>(null)

  const handleUpdateEncuesta = async (data: EncuestaUpdatePayload) => {
    await actualizarMutation.mutateAsync({ id: encuestaId, payload: data })
  }

  const handleCreateCampo = async (data: CampoCreatePayload) => {
    await crearCampoMutation.mutateAsync(data)
    setCampoDialogOpen(false)
  }

  const handleUpdateCampo = async (data: CampoUpdatePayload) => {
    if (!editingCampo) return
    await actualizarCampoMutation.mutateAsync({ campoId: editingCampo.id, payload: data as CampoUpdatePayload })
    setEditingCampo(null)
  }

  const handleDeleteCampo = async (campo: Campo) => {
    if (confirm(`¿Eliminar el campo "${campo.etiqueta}"?`)) {
      await eliminarCampoMutation.mutateAsync(campo.id)
    }
  }

  const handleEditCampo = (campo: Campo) => {
    setEditingCampo(campo)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!encuesta) {
    return (
      <div className="p-12 text-center">
        <p className="text-lg text-muted-foreground">Encuesta no encontrada</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/encuestas')}>Volver</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/encuestas')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Editar: {encuesta.titulo}</h1>
      </div>

      <div className="max-w-3xl">
        {/* Formulario de encuesta */}
        <EncuestaForm
          onSubmit={handleUpdateEncuesta}
          isSubmitting={actualizarMutation.isPending}
          encuesta={encuesta}
          mode="edit"
        />

        {/* Gestión de campos */}
        <Card className="mt-6 border border-gray-300 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Campos de la encuesta</CardTitle>
            {canEdit && (
              <Button size="sm" className="bg-teal-800 hover:bg-teal-900 text-white" onClick={() => setCampoDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" /> Nuevo campo
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {encuesta.campos.length > 0 ? (
              <div className="space-y-2">
                {encuesta.campos
                  .sort((a, b) => a.orden - b.orden)
                  .map((campo) => (
                    <CampoCard
                      key={campo.id}
                      campo={campo}
                      onEdit={handleEditCampo}
                      onDelete={handleDeleteCampo}
                      canEdit={canEdit}
                    />
                  ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No hay campos. Agrega el primer campo de la encuesta.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog crear campo */}
      <CampoDialog
        open={campoDialogOpen}
        onOpenChange={setCampoDialogOpen}
        onSubmit={handleCreateCampo}
        isSubmitting={crearCampoMutation.isPending}
        mode="create"
      />

      {/* Dialog editar campo */}
      <CampoDialog
        open={!!editingCampo}
        onOpenChange={(open) => { if (!open) setEditingCampo(null) }}
        onSubmit={handleUpdateCampo}
        isSubmitting={actualizarCampoMutation.isPending}
        campo={editingCampo}
        mode="edit"
      />
    </div>
  )
}
